import * as Sentry from '@sentry/react';
import { mergeRight } from 'rambda';
import { joinRelativeURL, joinURL } from 'ufo';
import Wretch from 'wretch';
import { queryStringAddon as WretchQueryStringAddon } from 'wretch/addons';
import WretchAbortAddon from 'wretch/addons/abort';
import WretchFormDataAddon from 'wretch/addons/formData';
import type { z } from 'zod';
import { container, DYNAMIC_ENDPOINT_MANAGER_TOKEN, I18N_TOKEN } from '@/container';
import { refreshToken, shouldRefreshToken } from '@/hooks/auth/use-refresh-token';
import { ResponseError, responseScheme } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { useConfigurationStore } from '@/stores/configuration';
import { addToast } from '@/stores/toast';
import { BASE_URL, BRAND_STORAGE_KEY } from '@/utils/constants';
import { getDeviceIdentifier } from '@/utils/get-standalone-mode';
import { createLogger } from '@/utils/logger';
import { Device } from './device-type';

interface FetcherOptions {
  target?: 'apiv1' | 'apiv2' | 'sso' | 'self' | 'external';
  signal?: AbortSignal;
  schema?: Nullable<z.ZodTypeAny>;
  cache?: RequestCache;
  headers?: Headers;
  validResponseCodes?: number[];
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head' | 'options';
  requireAuth?: boolean;
  includeExtras?: boolean;
  requestContentType?: 'form-data' | 'query' | 'json';
  responseContentType?: 'json' | 'text' | 'blob';
  formDataPayload?: Record<string, unknown>;
  timeout?: number;
}

const defaultOptions = Object.freeze({
  target: 'apiv1',
  method: 'post',
  cache: 'default',
  requireAuth: true,
  includeExtras: true,
  requestContentType: 'json',
  responseContentType: 'json',
} satisfies FetcherOptions);

const DEFAULT_TIMEOUT = 15_000;
const FORM_DATA_TIMEOUT = 30_000;

export async function fetcher<TData>(
  url: string,
  params?: Record<string, unknown>,
  incomingOptions?: FetcherOptions,
): Promise<TData> {
  const lang = container.get(I18N_TOKEN)?.language ?? 'en-US';
  const options = mergeRight<FetcherOptions, FetcherOptions>(defaultOptions, incomingOptions ?? {});
  const timeout = options.timeout ?? (options.requestContentType === 'form-data' ? FORM_DATA_TIMEOUT : DEFAULT_TIMEOUT);

  const endpoint = options.target === 'external' ? url : await getEndpoint(options.target);
  const headers = options.headers ?? new Headers();

  headers.append('X-Lang', lang);
  headers.append('X-Gateway', new URL(endpoint).hostname);

  const wretch = Wretch(endpoint)
    .addon(WretchFormDataAddon)
    .addon(WretchQueryStringAddon)
    .addon(WretchAbortAddon())
    .url(url, url.startsWith('http'))
    .options({ signal: options.signal, cache: options.cache })
    .headers(headers);

  const payload = { ...options.formDataPayload };

  const device = getDeviceIdentifier();
  if (options.includeExtras) {
    Object.assign(payload, { D: device });
  }

  const brand = localStorage.getItem(BRAND_STORAGE_KEY);
  if (brand && device !== Device.PWA) {
    Object.assign(payload, { Brand: brand });
  }

  if (options.requireAuth) {
    const auth = useAuthStore.getState().auth;

    if (auth) {
      Object.assign(payload, {
        UID: auth.UserID,
        Token: auth.Token,
      });
    } else {
      throw new Error('NOT_AUTHENTICATED');
    }
  }

  // biome-ignore lint/suspicious/noImplicitAnyLet: let typescript define its type
  let request;

  if (options.method && ['get', 'head'].includes(options.method)) {
    request = wretch.query({ ...params, ...payload }).fetch(options.method);
  } else {
    switch (options.requestContentType) {
      case 'query':
        request = wretch
          .query({ ...params, ...payload })
          .fetch(options.method)
          .setTimeout(timeout);
        break;
      case 'form-data':
        request = wretch
          .formData({ ...params, Data: JSON.stringify(payload) })
          .fetch(options.method)
          .setTimeout(timeout);
        break;
      default:
        request = wretch
          .json({ ...params, ...payload })
          .fetch(options.method)
          .setTimeout(timeout);
        break;
    }
  }

  let data: unknown;
  try {
    await Sentry.startSpan(
      {
        op: 'http.client',
        name: `${options.method} ${url}`,
      },
      async (span) => {
        span.setAttribute('http.request.method', options.method);
        span.setAttribute('server.address', new URL(endpoint).hostname);

        const response = await request.res();
        switch (options.responseContentType) {
          case 'text':
            data = await response.text();
            break;
          case 'blob':
            data = await response.blob();
            break;
          default:
            data = await response.json();
            break;
        }

        span.setAttribute('http.response.status_code', response.status);
        span.setAttribute('http.response_content_length', Number(response.headers.get('content-length')));
      },
    );
  } catch (err) {
    if (err instanceof Error) {
      if (err.name !== 'CancelledError' && err.name !== 'AbortError') {
        container.get(DYNAMIC_ENDPOINT_MANAGER_TOKEN)?.revokeApiEndpoint(endpoint);
        fetcherLogger.error(err);
      }

      throw err;
    }
  }

  if (options.requireAuth && data && typeof data === 'object' && 'Code' in data) {
    if (data.Code === 2) {
      if (shouldRefreshToken()) {
        await refreshToken();
        return fetcher(url, params, options);
      }

      useAuthStore.getState().stop();
      throw new Error('NOT_AUTHENTICATED');
    }
  }

  if (options?.schema) {
    const schema = options.schema;
    const parsedResult = await schema.safeParseAsync(data);

    if (!parsedResult.success) {
      fetcherLogger.error('schema check', url, data, parsedResult.error.issues);
      const parsedError = await responseScheme.strip().safeParseAsync(data);
      if (parsedError.success) {
        throw new ResponseError(parsedError.data.Code, parsedError.data.Msg);
      }
      throw data;
    }

    if (options.validResponseCodes) {
      if (!options.validResponseCodes.includes(parsedResult.data.Code)) {
        throw new ResponseError(parsedResult.data.Code, parsedResult.data.Msg);
      }
    }

    return parsedResult.data;
  }

  if (options.validResponseCodes) {
    const parsedResult = await responseScheme.safeParseAsync(data);
    if (parsedResult.success && !options.validResponseCodes.includes(parsedResult.data.Code)) {
      throw new ResponseError(parsedResult.data.Code, parsedResult.data.Msg);
    }
    if (!parsedResult.success) {
      fetcherLogger.error('schema check', url, data, parsedResult.error.issues);
    }
  }

  return data as TData;
}

async function getEndpoint(target: FetcherOptions['target'] = 'apiv1'): Promise<string> {
  let suffix = '/Api';
  switch (target) {
    case 'self':
      return location.protocol.startsWith('http') ? joinURL(location.origin, BASE_URL) : BASE_URL;
    case 'apiv2':
      suffix = '/v2/Api';
      break;
    case 'sso':
      suffix = '/Sso';
      break;
  }

  const endpoint = await new Promise<string>((resolve) => {
    const endpoint = useConfigurationStore.getState().endpoint.API_ENDPOINT;
    if (endpoint) {
      resolve(endpoint);
    } else {
      const unsubscribe = useConfigurationStore.subscribe(({ endpoint }) => {
        if (endpoint.API_ENDPOINT) {
          unsubscribe();
          resolve(endpoint.API_ENDPOINT);
        }
      });
    }
  });

  return joinRelativeURL(endpoint, '..', suffix);
}

const fetcherLogger = createLogger('Fetcher');

export function catchFetcherError(error: unknown) {
  if (ResponseError.isResponseError(error)) {
    addToast({
      type: 'error',
      message: error.message,
    });
  } else {
    fetcherLogger.error(error);
  }
}

export function wait(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();

  return new Promise((resolve) => setTimeout(resolve, ms));
}
